import type * as monacoTypes from 'monaco-editor';

export type ExpressionAutocomplete = {
  display: string;
  token: string;
  supportedValues?: string[];
  quoteSupportedValues?: boolean;
  /** Caller-supplied text shown in the autocomplete dropdown's right-hand column (e.g. a data type). */
  detail?: string;
  /** The keys of an object-valued token, offered once the user types `<token>.`. */
  properties?: ExpressionAutocompleteProperty[];
};

export type ExpressionAutocompleteProperty = {
  name: string;
  detail?: string;
  supportedValues?: string[];
};

// Per-model autocomplete data (weak keyed by ITextModel so GC can reclaim). `undefined` means the
// caller is still fetching its tokens.
export const modelAutocomplete = new WeakMap<monacoTypes.editor.ITextModel, ExpressionAutocomplete[] | undefined>();

// One provider for the whole app session
let expressionProviderRegistered = false;

export const registerExpressionLanguage = (monaco: typeof import('monaco-editor')) => {
  // Register custom language for expression editor
  monaco.languages.register({ id: 'expression-editor' });

  // Define tokens for syntax highlighting
  monaco.languages.setMonarchTokensProvider('expression-editor', {
    tokenizer: {
      root: [
        // This will be updated dynamically by each editor instance
        [/"[^"]*"/, 'string'],
        [/'[^']*'/, 'string'],
        [/\b\d+\.?\d*\b/, 'number'],
        [/[+\-*/=<>!&|?]/, 'operator'],
        [/[(),;]/, 'delimiter'],
        [/\./, 'delimiter'],
        [/\w+/, 'identifier'],
      ],
    },
  });
};

// Regex to detect when the cursor is in a "value context" — i.e. after an equality operator
// (`==` or `!=`) preceded by a known token. The opening quote is optional: if present, the
// user's quote style (`'` or `"`) is reused on insert; if absent, string suggestions are
// wrapped in `"…"` so the inserted value is a complete literal.
// Captures: [1] the token, [2] the opening quote (if any), [3] partial value when quoted,
// [4] partial value when unquoted (no whitespace, so it doesn't gobble later expression text).
const VALUE_CONTEXT_RE = /(\S+)\s*(?:==|!=)\s*(?:(['"])([^'"]*)?|([^'"\s]*))$/;

/**
 * Given the text before the cursor, checks if we are in a value-comparison position
 * (e.g. `data.xxx == "par` or `data.xxx == par`) and returns the matched token string,
 * the opening quote (if typed), and any partial value typed so far so the caller can
 * build a replacement range.
 */
const parseValueContext = (beforeCursor: string) => {
  const match = VALUE_CONTEXT_RE.exec(beforeCursor);
  if (!match) return null;
  const [, tokenStr, quote, quotedPartial, unquotedPartial] = match;
  const partial = quote ? (quotedPartial ?? '') : (unquotedPartial ?? '');
  return { tokenStr, quote, partial };
};

const formatSupportedValue = (value: string, quoteSupportedValues = true, quote?: string) => {
  if (!quoteSupportedValues) return value;

  const closingQuote = quote ?? '"';
  const openingQuote = quote ? '' : closingQuote;
  return `${openingQuote}${value}${closingQuote}`;
};

/** The token a comparison reads, which may be a property of an object token like `data.check.decision`. */
const findComparedToken = (tokens: ExpressionAutocomplete[], tokenStr: string): ExpressionAutocomplete | undefined => {
  const token = tokens.find(t => t.token === tokenStr);
  if (token) return token;
  for (const t of tokens) {
    const property = t.properties?.find(p => `${t.token}.${p.name}` === tokenStr);
    if (property) return { display: tokenStr, token: tokenStr, supportedValues: property.supportedValues };
  }
  return undefined;
};

/**
 * Keys of the object token the dotted prefix starts with. Falling through to the flat token list
 * instead would show nothing: no token's label starts with `<object token>.`, so Monaco filters
 * every one of them out.
 *
 * The longest matching token wins, and everything after it is the partial: a key can itself be a
 * dotted path (`id.ssn9`), so the partial spans dots and `data.x.id.ss` still resolves to `data.x`.
 */
const memberSuggestions = (
  monaco: typeof import('monaco-editor'),
  tokens: ExpressionAutocomplete[],
  dottedPrefix: string,
  cursor: { position: monacoTypes.Position; endColumn: number },
) => {
  const objectToken = tokens
    .filter(t => dottedPrefix.startsWith(`${t.token}.`))
    .sort((a, b) => b.token.length - a.token.length)[0];
  const properties = objectToken?.properties;
  if (!properties?.length) return null;

  const partial = dottedPrefix.slice(objectToken.token.length + 1);

  const { position, endColumn } = cursor;
  const range: monacoTypes.IRange = {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: position.column - partial.length,
    endColumn,
  };
  return properties.map(property => ({
    label: property.name,
    kind: monaco.languages.CompletionItemKind.Field,
    insertText: property.name,
    detail: property.detail,
    range,
  }));
};

export const ensureExpressionProvider = (monaco: typeof import('monaco-editor')) => {
  if (expressionProviderRegistered) return;

  // Register custom completion provider
  monaco.languages.registerCompletionItemProvider('expression-editor', {
    triggerCharacters: ['"', "'", '.', ' '],
    provideCompletionItems: (model, position) => {
      const tokens = modelAutocomplete.get(model);
      const lineContent = model.getLineContent(position.lineNumber);
      const beforeCursor = lineContent.substring(0, position.column - 1);

      // An empty range means accepting the row neither inserts nor deletes, and exempts it from
      // Monaco's filtering so a partial word doesn't hide it. `Variable` because `showWords: false`
      // hides the `Text` kind.
      if (!tokens) {
        const range: monacoTypes.IRange = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: position.column,
          endColumn: position.column,
        };
        const loading = {
          label: 'Loading...',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: '',
          range,
        };
        return { suggestions: [loading] };
      }

      // Check if the cursor is in a value context (after == or !=). If so, suggest the supported values for the display token
      const valueCtx = parseValueContext(beforeCursor);
      if (valueCtx) {
        const matchedToken = findComparedToken(tokens, valueCtx.tokenStr);
        if (matchedToken?.supportedValues?.length) {
          // Replace just the partial that's been typed so far. Quoted values preserve the
          // user's opening quote, while unquoted values replace it. When no quote was typed,
          // the range starts at the cursor (or beginning of the partial). Monaco columns are
          // 1-based.
          const removeOpeningQuote = matchedToken.quoteSupportedValues === false && valueCtx.quote ? 1 : 0;
          const range: monacoTypes.IRange = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: position.column - valueCtx.partial.length - removeOpeningQuote,
            endColumn: position.column,
          };

          const suggestions: monacoTypes.languages.CompletionItem[] = matchedToken.supportedValues.map((val, idx) => ({
            label: val,
            kind: monaco.languages.CompletionItemKind.Value,
            insertText: formatSupportedValue(val, matchedToken.quoteSupportedValues, valueCtx.quote),
            detail: matchedToken.display,
            range,
            sortText: idx.toString().padStart(4, '0'),
          }));

          return { suggestions };
        }
      }

      // The user just typed a space after a known token (no operator yet), and there's
      // nothing else on the rest of the line. Treat the space as a hint that they're about
      // to write a value comparison: offer the supportedValues with the equality operator
      // synthesized on insert. Requiring the trailing space avoids stealing the dropdown
      // while the user is still typing the token name.
      const afterCursor = lineContent.substring(position.column - 1);
      const tokenThenSpaceMatch = beforeCursor.match(/(\S+)\s+$/);
      if (tokenThenSpaceMatch && afterCursor.trim() === '') {
        const matchedToken = findComparedToken(tokens, tokenThenSpaceMatch[1]);
        if (matchedToken?.supportedValues?.length) {
          const range: monacoTypes.IRange = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: position.column,
            endColumn: position.column,
          };
          const suggestions: monacoTypes.languages.CompletionItem[] = matchedToken.supportedValues.map((val, idx) => ({
            label: val,
            kind: monaco.languages.CompletionItemKind.Value,
            insertText: `== ${formatSupportedValue(val, matchedToken.quoteSupportedValues)}`,
            detail: matchedToken.display,
            range,
            sortText: idx.toString().padStart(4, '0'),
          }));
          return { suggestions };
        }
      }

      // Walk backwards to find the full dotted prefix (e.g. "data.val" not just "val")
      const dottedMatch = beforeCursor.match(/([\w.]+)$/);
      const wordAtPos = model.getWordAtPosition(position);

      // If the word at the position is a known object token, suggest all the sub-fields on the object
      const endColumn = wordAtPos ? wordAtPos.endColumn : position.column;
      const members = dottedMatch && memberSuggestions(monaco, tokens, dottedMatch[1], { position, endColumn });
      if (members) return { suggestions: members };

      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: dottedMatch ? position.column - dottedMatch[1].length : position.column,
        endColumn,
      };

      const suggestions: monacoTypes.languages.CompletionItem[] = tokens.map(item => ({
        label: item.display,
        kind: monaco.languages.CompletionItemKind.Variable,
        insertText: item.token,
        detail: item.detail,
        range,
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      }));

      return { suggestions };
    },
  });
  expressionProviderRegistered = true;
};
